// src/pages/ProductsPage.js
import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Link, Chip, Divider, Paper } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TextProvider } from '../context/TextContext';
import arrangellyLogo from '../assets/products/arrangelly.png';
import trafohubLogo from '../assets/products/trafohub.png';
import violynningLogo from '../assets/products/violynning.png';

const ProductLogo = ({ src, alt }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: { xs: 160, md: 200 },
      backgroundColor: '#FAF9F8',
      borderBottom: '1px solid #e0e0e0',
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        maxWidth: '70%',
        maxHeight: '80%',
        objectFit: 'contain',
      }}
    />
  </Box>
);

const ProductsPage = () => {
  return (
    <TextProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F3F2F1' }}>
        <Header />
        <Container sx={{ padding: 4, flex: 1 }}>
          <Typography variant="h3" gutterBottom sx={{ color: '#0078D4' }}>
            Products
          </Typography>
          <Typography variant="body1" paragraph sx={{ marginBottom: 4 }}>
            Explore the innovative products I&apos;ve developed and contributed to.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: '100%', overflow: 'hidden' }}>
                <ProductLogo src={arrangellyLogo} alt="Arrangelly logo" />
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                        Arrangelly
                      </Typography>
                      <Chip label="Staging" size="small" color="warning" sx={{ marginBottom: 1 }} />
                    </Box>
                    <Link
                      href="https://staging.arrangelly.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none' }}
                    >
                      <Chip label="Visit Staging →" color="primary" clickable sx={{ fontSize: '0.9rem', padding: '8px 16px' }} />
                    </Link>
                  </Box>

                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', marginBottom: 3 }}>
                    A premium all-in-one platform for music teachers to run and grow their studio. Arrangelly streamlines
                    lessons, student management, bookings, payments, and content—so educators can focus on teaching
                    while the platform handles the operational rhythm of a modern music business.
                  </Typography>

                  <Divider sx={{ marginY: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          🎯 Teaching Suite
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Bookings, students, and courses in one workspace</li>
                          <li style={{ marginBottom: '8px' }}>✓ Calendar and schedule with auto-generated meeting links</li>
                          <li style={{ marginBottom: '8px' }}>✓ Payments and lesson credits integrated with bookings</li>
                          <li style={{ marginBottom: '8px' }}>✓ Newsletter and blog without a second login</li>
                          <li style={{ marginBottom: '8px' }}>✓ Analytics for revenue, activity, and studio growth</li>
                          <li style={{ marginBottom: '8px' }}>✓ AI exercises to support modern music teaching</li>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          🔗 Core Integrations
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Payments tied to lesson credits and bookings</li>
                          <li style={{ marginBottom: '8px' }}>✓ Meeting links generated from scheduled sessions</li>
                          <li style={{ marginBottom: '8px' }}>✓ Newsletter and content tools in sync with the studio</li>
                          <li style={{ marginBottom: '8px' }}>✓ End-to-end flow from slot booking to live session</li>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ marginY: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    🛠️ Tech Stack & Tools
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label="React" color="primary" variant="outlined" />
                    <Chip label=".NET" color="primary" variant="outlined" />
                    <Chip label="Docker" color="primary" variant="outlined" />
                    <Chip label="PostgreSQL" color="primary" variant="outlined" />
                    <Chip label="Git" color="primary" variant="outlined" />
                    <Chip label="GitHub Actions" color="primary" variant="outlined" />
                    <Chip label="VPS Hosting" color="primary" variant="outlined" />
                    <Chip label="Payments" color="primary" variant="outlined" />
                    <Chip label="Video Meetings" color="primary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, marginBottom: 1 }}>
                    Exposly
                  </Typography>
                  <Chip label="VPS Cockpit" size="small" color="primary" sx={{ marginBottom: 2 }} />

                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', marginBottom: 3 }}>
                    A custom VPS Cockpit for deployment and operations—manage all applications on a server from one
                    place. Exposly covers deployments, live logs and monitoring, TLS certificate lifecycle, and day-to-day
                    VPS administration so you can ship and operate services without jumping between SSH sessions and
                    scattered tools.
                  </Typography>

                  <Divider sx={{ marginY: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          🚀 App & Deployment Control
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Manage all applications hosted on the VPS</li>
                          <li style={{ marginBottom: '8px' }}>✓ Deploy and operate services from a single cockpit</li>
                          <li style={{ marginBottom: '8px' }}>✓ Centralized control instead of ad-hoc SSH workflows</li>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          📡 Ops, Logs & Certificates
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Logs and monitoring for running applications</li>
                          <li style={{ marginBottom: '8px' }}>✓ Certificate management (issue, renew, track)</li>
                          <li style={{ marginBottom: '8px' }}>✓ Broader VPS administration from the same UI</li>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ marginY: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    🛠️ Tech Stack & Tools
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label="React" color="primary" variant="outlined" />
                    <Chip label=".NET" color="primary" variant="outlined" />
                    <Chip label="Docker" color="primary" variant="outlined" />
                    <Chip label="VPS Hosting" color="primary" variant="outlined" />
                    <Chip label="Linux" color="primary" variant="outlined" />
                    <Chip label="Nginx" color="primary" variant="outlined" />
                    <Chip label="TLS / Certificates" color="primary" variant="outlined" />
                    <Chip label="Monitoring" color="primary" variant="outlined" />
                    <Chip label="Logging" color="primary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ height: '100%', overflow: 'hidden' }}>
                <ProductLogo src={trafohubLogo} alt="TrafoHub logo" />
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                      TrafoHub
                    </Typography>
                    <Link
                      href="https://trafohub.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none' }}
                    >
                      <Chip label="Visit Website →" color="primary" clickable sx={{ fontSize: '0.9rem', padding: '8px 16px' }} />
                    </Link>
                  </Box>

                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', marginBottom: 3 }}>
                    A B2B marketplace for power transformers—browse verified inventory from trusted factory partners,
                    request quotes, and buy with confidence. Every listing is admin-approved before it goes live,
                    giving buyers a reliable path from discovery to purchase.
                  </Typography>

                  <Divider sx={{ marginY: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          ⚡ Marketplace Features
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Browse approved transformer inventory</li>
                          <li style={{ marginBottom: '8px' }}>✓ Request quotes and purchase with confidence</li>
                          <li style={{ marginBottom: '8px' }}>✓ Admin-verified listings from factory partners</li>
                          <li style={{ marginBottom: '8px' }}>✓ Trusted partner network before products go live</li>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          🏭 For Buyers & Partners
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Clear path from listing discovery to quote</li>
                          <li style={{ marginBottom: '8px' }}>✓ Factory partners supply verified stock</li>
                          <li style={{ marginBottom: '8px' }}>✓ Admin approval gate for quality control</li>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ marginY: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    🛠️ Tech Stack & Tools
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label="React" color="primary" variant="outlined" />
                    <Chip label=".NET" color="primary" variant="outlined" />
                    <Chip label="Docker" color="primary" variant="outlined" />
                    <Chip label="PostgreSQL" color="primary" variant="outlined" />
                    <Chip label="Git" color="primary" variant="outlined" />
                    <Chip label="GitHub Actions" color="primary" variant="outlined" />
                    <Chip label="VPS Hosting" color="primary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ height: '100%', overflow: 'hidden' }}>
                <ProductLogo src={violynningLogo} alt="Violynning logo" />
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                      Violynning
                    </Typography>
                    <Link
                      href="https://violynning.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none' }}
                    >
                      <Chip label="Visit Website →" color="primary" clickable sx={{ fontSize: '0.9rem', padding: '8px 16px' }} />
                    </Link>
                  </Box>

                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', marginBottom: 3 }}>
                    A comprehensive, fully functional educational platform designed to bridge the gap between teachers and students.
                    Violynning provides a complete ecosystem for music education, featuring powerful administrative tools and an intuitive
                    user experience that empowers educators to manage their teaching business while offering students seamless access to
                    lessons, subscriptions, and educational content.
                  </Typography>

                  <Divider sx={{ marginY: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          🎯 Admin Dashboard
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Custom product creation and management</li>
                          <li style={{ marginBottom: '8px' }}>✓ Stripe subscription integration and management</li>
                          <li style={{ marginBottom: '8px' }}>✓ Custom calendar system for scheduling</li>
                          <li style={{ marginBottom: '8px' }}>✓ Advanced booking management system</li>
                          <li style={{ marginBottom: '8px' }}>✓ Zoom schedule integration</li>
                          <li style={{ marginBottom: '8px' }}>✓ Comprehensive analytics and reporting dashboard</li>
                          <li style={{ marginBottom: '8px' }}>✓ Video management with categories and subscriptions</li>
                          <li style={{ marginBottom: '8px' }}>✓ Newsletter management system</li>
                          <li style={{ marginBottom: '8px' }}>✓ Blog management and content creation</li>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          👤 User Dashboard
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Intuitive lesson booking system</li>
                          <li style={{ marginBottom: '8px' }}>✓ Subscription purchase and management</li>
                          <li style={{ marginBottom: '8px' }}>✓ Complete profile control and customization</li>
                          <li style={{ marginBottom: '8px' }}>✓ Access to video library and educational content</li>
                          <li style={{ marginBottom: '8px' }}>✓ Real-time lesson scheduling and notifications</li>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ marginY: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    🛠️ Tech Stack & Tools
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label="React" color="primary" variant="outlined" />
                    <Chip label=".NET" color="primary" variant="outlined" />
                    <Chip label="Docker" color="primary" variant="outlined" />
                    <Chip label="Cursor AI" color="primary" variant="outlined" />
                    <Chip label="Git" color="primary" variant="outlined" />
                    <Chip label="GitHub Actions" color="primary" variant="outlined" />
                    <Chip label="VPS Hosting" color="primary" variant="outlined" />
                    <Chip label="Supabase" color="primary" variant="outlined" />
                    <Chip label="PostgreSQL" color="primary" variant="outlined" />
                    <Chip label="Stripe" color="primary" variant="outlined" />
                    <Chip label="Zoom API" color="primary" variant="outlined" />
                    <Chip label="SMTP" color="primary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    VoiceSwap
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', marginBottom: 3 }}>
                    A local Python voice conversion app built on RVC for training character and singer voices, then
                    mixing them with an interpreter track or fully replacing spoken parts. VoiceSwap already serves
                    10+ customers, with more than 20 real singers&apos; voices trained and 20k+ training epochs completed—
                    ideal for character work, localization, and creative voice production without sending audio to the cloud.
                  </Typography>

                  <Divider sx={{ marginY: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#0078D4', fontWeight: 700 }}>10+</Typography>
                        <Typography variant="body2" color="text.secondary">Active customers</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#0078D4', fontWeight: 700 }}>20+</Typography>
                        <Typography variant="body2" color="text.secondary">Real singers&apos; voices trained</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#0078D4', fontWeight: 700 }}>20k+</Typography>
                        <Typography variant="body2" color="text.secondary">Training epochs</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ marginY: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          🎙️ Capabilities
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Local training of character and singer voice models</li>
                          <li style={{ marginBottom: '8px' }}>✓ Mix converted voices with an interpreter track</li>
                          <li style={{ marginBottom: '8px' }}>✓ Replace original voices while preserving timing</li>
                          <li style={{ marginBottom: '8px' }}>✓ Fully offline workflow for privacy and control</li>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#0078D4', fontWeight: 600, marginBottom: 2 }}>
                          🎯 Use Cases
                        </Typography>
                        <Box component="ul" sx={{ margin: 0, paddingLeft: 2, listStyle: 'none' }}>
                          <li style={{ marginBottom: '8px' }}>✓ Character voice creation for media and games</li>
                          <li style={{ marginBottom: '8px' }}>✓ Interpreter / dual-voice mixing workflows</li>
                          <li style={{ marginBottom: '8px' }}>✓ Voice replacement for localization and dubbing</li>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ marginY: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    🛠️ Tech Stack & Tools
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label="Python" color="primary" variant="outlined" />
                    <Chip label="RVC" color="primary" variant="outlined" />
                    <Chip label="Machine Learning" color="primary" variant="outlined" />
                    <Chip label="Audio Processing" color="primary" variant="outlined" />
                    <Chip label="Local / Offline" color="primary" variant="outlined" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    Inceptionade
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', marginBottom: 3 }}>
                    An innovative AI-powered offline application that revolutionizes product development by generating comprehensive
                    documentation and specifications through natural language prompting. Simply describe your product idea, and
                    Inceptionade intelligently generates all critical aspects needed to bring your vision to life, from technical
                    architecture to business strategy.
                  </Typography>

                  <Divider sx={{ marginY: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    🤖 AI-Generated Aspects
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ marginBottom: 2, color: 'text.secondary' }}>
                    Inceptionade leverages advanced LLM integrations to generate context-specific documentation based on the aspect type:
                  </Typography>
                  <Grid container spacing={1} sx={{ marginTop: 1, marginBottom: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Architecture" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Database Schema" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="API Specifications" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="User Flow" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Business Model" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Product Roadmap" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Delivery Roadmap" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Product Backlog" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Delivery Plan" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Marketing Strategy" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Brand Identity" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Sales Strategy" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Pitch Deck" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Chip label="Team Structure" variant="outlined" sx={{ margin: 0.5 }} />
                    </Grid>
                  </Grid>

                  <Paper elevation={0} sx={{ padding: 2, backgroundColor: 'rgba(0, 120, 212, 0.08)', marginBottom: 3, borderLeft: '3px solid #0078D4' }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#005A9E' }}>
                      💡 Each aspect type intelligently selects the most appropriate LLM based on the complexity and domain requirements,
                      ensuring optimal quality and relevance for technical documentation, business strategy, or creative content.
                    </Typography>
                  </Paper>

                  <Divider sx={{ marginY: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, marginBottom: 2 }}>
                    🛠️ Tech Stack & Tools
                  </Typography>
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, marginTop: 1 }}>
                      Core Technologies:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: 2 }}>
                      <Chip label="React" color="primary" variant="outlined" />
                      <Chip label=".NET" color="primary" variant="outlined" />
                      <Chip label="Docker" color="primary" variant="outlined" />
                      <Chip label="Cursor AI" color="primary" variant="outlined" />
                      <Chip label="Git" color="primary" variant="outlined" />
                      <Chip label="GitHub Actions" color="primary" variant="outlined" />
                      <Chip label="VPS Hosting" color="primary" variant="outlined" />
                      <Chip label="Supabase" color="primary" variant="outlined" />
                      <Chip label="PostgreSQL" color="primary" variant="outlined" />
                      <Chip label="pgvector" color="primary" variant="outlined" />
                      <Chip label="Qdrant" color="primary" variant="outlined" />
                      <Chip label="Python" color="primary" variant="outlined" />
                      <Chip label="Node.js" color="primary" variant="outlined" />
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, marginTop: 1 }}>
                      LLM Integrations:
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ color: 'text.secondary', marginBottom: 1 }}>
                      Dynamic LLM selection based on aspect requirements:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="OpenAI GPT" color="secondary" variant="outlined" />
                      <Chip label="Claude (Anthropic)" color="secondary" variant="outlined" />
                      <Chip label="Gemini" color="secondary" variant="outlined" />
                      <Chip label="Llama" color="secondary" variant="outlined" />
                      <Chip label="Custom LLM APIs" color="secondary" variant="outlined" />
                    </Box>
                    <Typography variant="body2" sx={{ marginTop: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                      The system intelligently routes each aspect generation request to the most suitable LLM, optimizing for
                      technical accuracy, creative output, or strategic planning based on the aspect type.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
        <Footer />
      </Box>
    </TextProvider>
  );
};

export default ProductsPage;
